import React, { useContext, useEffect } from 'react';

import { Box, HStack, Icon, Text } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';
import { getUSState } from 'features/store/utils';
import { useIntl } from 'react-intl';
import { Linking } from 'react-native';

import { IAddress, IStoreDetails } from '@rbi-ctg/menu';
import { StoreSanityOrderEta } from 'components/sanity-store-order-eta/store-sanity-order-eta';
import StaticMap from 'components/static-map';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useFlyBuyContext } from 'state/flybuy';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { SmartAlertsContext } from 'state/smart-alert';

import {
  Address,
  BoldText,
  Directions,
  MapContainer,
  PickUpTimeContainer,
  PickUpWindowAlert,
  PickUpWindowAlertContainer,
  PressableStoreContainer,
} from './styled';
import { getMapUrl } from './utils';

interface IMapCard {
  storeAddress: IAddress;
  storeDetails: IStoreDetails;
  orderDate?: Date | null;
  serverOrderServiceMode?: ServiceMode;
}

const MapCard = ({
  storeAddress,
  storeDetails,
  orderDate = null,
  serverOrderServiceMode,
}: IMapCard) => {
  const { formatMessage } = useIntl();
  const { isReadyWithTimedFire: isFlyBuyReadyWithTimedFire } = useFlyBuyContext();
  const { isOrderBayEnabled, serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();
  const { orderSmartAlert, setServerOrderServiceMode, setServerOrderStoreId } = useContext(
    SmartAlertsContext
  );

  const isOrderBayOrder = isOrderBayEnabled && serviceMode === ServiceMode.CURBSIDE;

  // Store Address Info
  const city = storeAddress?.city ?? '';
  const state = getUSState(storeAddress.state ?? '');
  const postalCode = storeAddress.zip.split('-')[0];
  const directions = formatMessage({ id: 'directions' });
  const addressLine = storeAddress.addressLine1 ?? '';
  const addressAccessibilityLabel = `${addressLine}, ${city} ${
    state ? ` ${state}` : ''
  }, ${postalCode} ${directions}`;
  // Store coordinates
  const lat = storeDetails.latitude ?? 0;
  const lng = storeDetails.longitude ?? 0;

  let formattedOrderTime = '';
  let formattedOrderDate = '';
  const locale = formatMessage({ id: 'localeDateTime' });

  if (orderDate) {
    formattedOrderDate = orderDate.toLocaleString(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    formattedOrderTime = orderDate.toLocaleString(locale, {
      hour: 'numeric',
      minute: 'numeric',
    });
  }

  const openMapUrl = React.useCallback(() => {
    logEvent(CustomEventNames.STORE_MAP_GET_DIRECTIONS, EventTypes.Other);
    const url = getMapUrl({ coordinates: `${lat},${lng}`, label: addressLine });

    if (url) {
      Linking.openURL(url);
    }
  }, [addressLine, lat, lng]);

  useEffect(() => {
    setServerOrderServiceMode(serverOrderServiceMode ?? null);
    setServerOrderStoreId(serverOrderServiceMode ? storeDetails.storeNumber : null);
  }, [
    serverOrderServiceMode,
    setServerOrderServiceMode,
    setServerOrderStoreId,
    storeDetails.storeNumber,
  ]);

  return (
    <Box>
      <MapContainer>
        <StaticMap
          position={{
            lat,
            lng,
          }}
          enablePointerEvents
        />
      </MapContainer>
      <PressableStoreContainer
        accessibilityRole="button"
        accessibilityHint={formatMessage({ id: 'viewOnMap' })}
        accessibilityLabel={addressAccessibilityLabel}
        onPress={openMapUrl}
        borderBottomRadius={0}
      >
        <Box w={'full'}>
          <BoldText>{addressLine}</BoldText>
          <Address>{`${city}${state ? ` ${state}` : ''}, ${postalCode}`}</Address>
          <HStack justifyContent={'space-between'}>
            <Directions>{directions}</Directions>
            <Box maxWidth={'$150'}>
              <StoreSanityOrderEta store={store} />
            </Box>
          </HStack>
        </Box>
      </PressableStoreContainer>
      {
        // TODO: BKPE-6812 ORDER_BAY_PILOT. Remove or consolidate this logic once the pilot is done
        orderDate && !isFlyBuyReadyWithTimedFire && !isOrderBayOrder && (
          <>
            <PickUpTimeContainer showBorderRadius={!orderSmartAlert}>
              <Text variant="copyOne" textTransform="capitalize">
                {formatMessage({ id: 'pickUpTime' })}
              </Text>
              <BoldText mt="$1">{formattedOrderTime}</BoldText>
              <Text variant="copyOne" mt="$1">
                {formattedOrderDate}
              </Text>
            </PickUpTimeContainer>
            {!!orderSmartAlert && (
              <PickUpWindowAlertContainer>
                <Icon variant="warning" size="xs" />
                <PickUpWindowAlert>{orderSmartAlert.message}</PickUpWindowAlert>
              </PickUpWindowAlertContainer>
            )}
          </>
        )
      }
    </Box>
  );
};

export default MapCard;
